import type { FormError, StringRecord, TypedAxiosError } from '@types';

/**
 * Standardized error types for consistent error handling
 */
export enum ErrorType {
  VALIDATION = 'VALIDATION_ERROR',
  NETWORK = 'NETWORK_ERROR',
  AUTHENTICATION = 'AUTHENTICATION_ERROR',
  AUTHORIZATION = 'AUTHORIZATION_ERROR',
  NOT_FOUND = 'NOT_FOUND_ERROR',
  SERVER = 'SERVER_ERROR',
  TIMEOUT = 'TIMEOUT_ERROR',
  UNKNOWN = 'UNKNOWN_ERROR',
}

/**
 * Enhanced error interface with additional context
 */
export interface EnhancedError extends FormError {
  type: ErrorType;
  timestamp: number;
  context?: StringRecord;
  stack?: string;
  retry?: boolean;
}

/**
 * Error handling configuration
 */
export interface ErrorHandlerConfig {
  /** Whether to log errors to console */
  enableLogging?: boolean;
  /** Whether to include stack traces in development */
  includeStackTrace?: boolean;
  /** Custom error message formatters */
  messageFormatters?: Record<string, (error: EnhancedError) => string>;
  /** Default retry configuration */
  defaultRetry?: boolean;
}

/**
 * Default error handler configuration
 */
const DEFAULT_CONFIG: Required<ErrorHandlerConfig> = {
  enableLogging: process.env.NODE_ENV === 'development',
  includeStackTrace: process.env.NODE_ENV === 'development',
  messageFormatters: {},
  defaultRetry: false,
};

/**
 * Centralized error handler for consistent error processing
 */
class ErrorHandler {
  private config: Required<ErrorHandlerConfig>;

  constructor(config: ErrorHandlerConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Processes any error type and returns a standardized EnhancedError
   *
   * @param error - The error to process
   * @param context - Additional context information
   * @returns Standardized enhanced error object
   */
  handle(error: unknown, context?: StringRecord): EnhancedError {
    const enhancedError = this.createEnhancedError(error, context);

    if (this.config.enableLogging) {
      this.logError(enhancedError);
    }

    return enhancedError;
  }

  /**
   * Creates an enhanced error from any error type
   */
  private createEnhancedError(error: unknown, context?: StringRecord): EnhancedError {
    const baseError: EnhancedError = {
      message: 'An unknown error occurred',
      type: ErrorType.UNKNOWN,
      timestamp: Date.now(),
      context,
      retry: this.config.defaultRetry,
    };

    // Handle Axios errors
    if (this.isAxiosError(error)) {
      return this.handleAxiosError(error, baseError);
    }

    // Handle JavaScript errors
    if (error instanceof Error) {
      return this.handleJavaScriptError(error, baseError);
    }

    // Handle string errors
    if (typeof error === 'string') {
      return {
        ...baseError,
        message: error,
      };
    }

    // Handle object errors
    if (typeof error === 'object' && error !== null) {
      return this.handleObjectError(error as any, baseError);
    }

    return baseError;
  }

  /**
   * Handles Axios HTTP errors
   */
  private handleAxiosError(error: TypedAxiosError, baseError: EnhancedError): EnhancedError {
    const { response, request, code } = error;

    if (response) {
      // Server responded with error status
      const status = response.status;
      const data = response.data;

      return {
        ...baseError,
        message: data?.message ?? error.message,
        code: data?.code ?? data?.status_code ?? status.toString(),
        type: this.getErrorTypeFromStatus(status),
        details: data?.details || { status, data },
        retry: this.shouldRetryForStatus(status),
      };
    }

    if (request) {
      // Request was made but no response received
      return {
        ...baseError,
        message: 'Network error - no response received',
        code: code ?? 'NETWORK_ERROR',
        type: ErrorType.NETWORK,
        retry: true,
      };
    }

    // Request setup error
    return {
      ...baseError,
      message: error.message,
      code: code ?? 'REQUEST_SETUP_ERROR',
      type: ErrorType.UNKNOWN,
    };
  }

  /**
   * Handles native JavaScript errors
   */
  private handleJavaScriptError(error: Error, baseError: EnhancedError): EnhancedError {
    return {
      ...baseError,
      message: error.message,
      code: error.name,
      type: this.getErrorTypeFromName(error.name),
      stack: this.config.includeStackTrace ? error.stack : undefined,
    };
  }

  /**
   * Handles object-type errors
   */
  private handleObjectError(error: Record<string, unknown>, baseError: EnhancedError): EnhancedError {
    return {
      ...baseError,
      message: String(error.message || error.error || 'Object error'),
      code: String(error.code || error.type || 'OBJECT_ERROR'),
      details: error,
    };
  }

  /**
   * Maps HTTP status codes to error types
   */
  private getErrorTypeFromStatus(status: number): ErrorType {
    if (status >= 400 && status < 500) {
      switch (status) {
        case 401:
          return ErrorType.AUTHENTICATION;
        case 403:
          return ErrorType.AUTHORIZATION;
        case 404:
          return ErrorType.NOT_FOUND;
        case 408:
          return ErrorType.TIMEOUT;
        case 422:
          return ErrorType.VALIDATION;
        default:
          return ErrorType.VALIDATION;
      }
    }

    if (status >= 500) {
      return ErrorType.SERVER;
    }

    return ErrorType.UNKNOWN;
  }

  /**
   * Maps JavaScript error names to error types
   */
  private getErrorTypeFromName(name: string): ErrorType {
    switch (name) {
      case 'ValidationError':
        return ErrorType.VALIDATION;
      case 'NetworkError':
        return ErrorType.NETWORK;
      case 'TimeoutError':
        return ErrorType.TIMEOUT;
      default:
        return ErrorType.UNKNOWN;
    }
  }

  /**
   * Determines if request should be retried based on status
   */
  private shouldRetryForStatus(status: number): boolean {
    // Retry on server errors and specific client errors
    return status >= 500 || status === 408 || status === 429;
  }

  /**
   * Type guard for Axios errors
   */
  private isAxiosError(error: unknown): error is TypedAxiosError {
    return typeof error === 'object' && error !== null && 'isAxiosError' in error && error.isAxiosError === true;
  }

  /**
   * Logs error information
   */
  private logError(error: EnhancedError): void {
    const logData = {
      type: error.type,
      message: error.message,
      code: error.code,
      timestamp: new Date(error.timestamp).toISOString(),
      context: error.context,
    };

    if (error.type === ErrorType.SERVER || error.type === ErrorType.UNKNOWN) {
      console.error('Error Handler:', logData, error.stack);
    } else {
      console.warn('Error Handler:', logData);
    }
  }

  /**
   * Updates error handler configuration
   */
  updateConfig(newConfig: Partial<ErrorHandlerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Formats error for user display
   */
  formatForUser(error: EnhancedError): string {
    const formatter = this.config.messageFormatters[error.type];

    if (formatter) {
      return formatter(error);
    }

    // Default user-friendly messages
    switch (error.type) {
      case ErrorType.NETWORK:
        return 'Connection error. Please check your internet connection.';
      case ErrorType.AUTHENTICATION:
        return 'Authentication required. Please log in again.';
      case ErrorType.AUTHORIZATION:
        return 'You do not have permission to perform this action.';
      case ErrorType.NOT_FOUND:
        return 'The requested resource was not found.';
      case ErrorType.VALIDATION:
        return error.message || 'Validation error. Please check your input.';
      case ErrorType.TIMEOUT:
        return 'Request timed out. Please try again.';
      case ErrorType.SERVER:
        return 'Server error. Please try again later.';
      default:
        return error.message || 'An unexpected error occurred.';
    }
  }
}

/**
 * Global error handler instance
 */
export const errorHandler = new ErrorHandler();

/**
 * Convenience function for handling errors
 *
 * @param error - Error to handle
 * @param context - Additional context
 * @returns Enhanced error object
 */
export const handleError = (error: unknown, context?: StringRecord): EnhancedError => {
  return errorHandler.handle(error, context);
};

/**
 * Convenience function for creating form errors with enhanced handling
 *
 * @param error - Error to convert
 * @param context - Additional context
 * @returns Form error object
 */
export const createFormError = (error: unknown, context?: StringRecord): FormError => {
  const enhancedError = handleError(error, context);
  return {
    message: enhancedError.message,
    code: enhancedError.code,
    field: enhancedError.field,
    details: enhancedError.details,
  };
};
